/**
 * 管理端点餐提醒：
 * 1. WebSocket 新单/取消推送 → 立即提示一次
 * 2. 定时（默认 3 分钟）检查待确认/待审取消，有则各提示一次
 */
import { Message } from 'view-design'
import {
  getMerchantAlertConfig,
  getMerchantAlertSnapshot,
} from '@/api/merchantAlert'
import {
  connectMerchantAlertWs,
  disconnectMerchantAlertWs,
} from '@/utils/merchant-alert-ws'

const UNLOCK_SESSION_KEY = 'merchant_alert_web_audio_unlocked'
const DEFAULT_CHECK_MS = 180 * 1000
const WS_DEBOUNCE_MS = 2000

let timer = null
let checkIntervalMs = DEFAULT_CHECK_MS
let baselineReady = false
let lastWsNewAt = 0
let lastWsCancelAt = 0
let config = null
let unlocked = false
let autoUnlockBound = false
let audioNewEl = null
let audioCancelEl = null

function readUnlockSession() {
  try {
    return sessionStorage.getItem(UNLOCK_SESSION_KEY) === '1'
  } catch (e) {
    return false
  }
}

function saveUnlockSession() {
  try {
    sessionStorage.setItem(UNLOCK_SESSION_KEY, '1')
  } catch (e) {
    /* ignore */
  }
}

export function isMerchantWebAudioUnlocked() {
  return unlocked || readUnlockSession()
}

function rebuildAudioElements() {
  audioNewEl = null
  audioCancelEl = null
  if (!config) return
  if (config.new_order_sound) {
    audioNewEl = new Audio(config.new_order_sound)
    audioNewEl.preload = 'auto'
  }
  if (config.cancel_sound) {
    audioCancelEl = new Audio(config.cancel_sound)
    audioCancelEl.preload = 'auto'
  }
}

async function playAlertSound(type) {
  if (!unlocked || !config) return false
  const el = type === 'cancel' ? audioCancelEl : audioNewEl
  const url = type === 'cancel' ? config.cancel_sound : config.new_order_sound
  if (!url) return false
  try {
    const node = el || new Audio(url)
    node.volume = 1
    node.currentTime = 0
    await node.play()
    return true
  } catch (e) {
    return false
  }
}

async function loadConfig() {
  const res = await getMerchantAlertConfig()
  if (res.data && res.data.code === 200 && res.data.data) {
    config = res.data.data
    const sec =
      Number(config.repeat_remind_interval_sec) ||
      Number(config.poll_interval_sec) ||
      180
    checkIntervalMs = Math.min(600, Math.max(60, sec)) * 1000
    rebuildAudioElements()
    return config
  }
  return null
}

function showAlert(type, count, isRepeat) {
  const repeatHint = isRepeat ? '（仍未处理）' : ''
  const unlockHint = unlocked ? '' : '，请点任意处或「点餐提醒音」解锁声音'
  if (type === 'newOrder') {
    const n = count > 0 ? count : 1
    Message.info({
      content: `有待确认的点餐订单${n > 1 ? `（${n} 笔）` : ''}${repeatHint}${unlockHint}`,
      duration: 6,
    })
  } else {
    const n = count > 0 ? count : 1
    Message.warning({
      content: `有待处理的取消申请${n > 1 ? `（${n} 笔）` : ''}${repeatHint}`,
      duration: 6,
    })
  }
}

function emitAlert(type, count, isRepeat) {
  showAlert(type, count, isRepeat)
  playAlertSound(type)
}

/** WebSocket：立即提醒 */
function onWsMerchantAlert(alertType) {
  if (!config || !config.web_enabled) return
  const now = Date.now()
  if (alertType === 'cancel') {
    if (now - lastWsCancelAt < WS_DEBOUNCE_MS) return
    lastWsCancelAt = now
    emitAlert('cancel', 1, false)
    return
  }
  if (alertType === 'newOrder' || !alertType) {
    if (now - lastWsNewAt < WS_DEBOUNCE_MS) return
    lastWsNewAt = now
    emitAlert('newOrder', 1, false)
  }
}

/** 定时检查：有待确认/待审取消各提示一次 */
function evaluatePeriodicCheck(pendingCount, cancelCount, isBaseline) {
  if (isBaseline) return
  if (pendingCount > 0) {
    emitAlert('newOrder', pendingCount, true)
  }
  if (cancelCount > 0) {
    emitAlert('cancel', cancelCount, true)
  }
}

async function runPeriodicCheck() {
  if (!config || !config.web_enabled) return
  try {
    const res = await getMerchantAlertSnapshot()
    if (!(res.data && res.data.code === 200 && res.data.data)) return
    const snap = res.data.data
    const pendingCount = (snap.new_order_ids || []).length
    const cancelCount = (snap.cancel_review_ids || []).length

    if (!baselineReady) {
      baselineReady = true
      return
    }

    evaluatePeriodicCheck(pendingCount, cancelCount, false)
  } catch (e) {
    /* 静默 */
  }
}

export function primeMerchantWebAudio() {
  unlocked = true
  saveUnlockSession()
  rebuildAudioElements()
  if (audioNewEl) {
    audioNewEl.volume = 0.01
    audioNewEl.play().catch(() => {})
    audioNewEl.volume = 1
  }
}

export function bindMerchantWebAudioAutoUnlock() {
  if (autoUnlockBound || typeof document === 'undefined') return
  autoUnlockBound = true
  if (readUnlockSession()) {
    unlocked = true
  }
  const once = () => {
    if (!unlocked) primeMerchantWebAudio()
    document.removeEventListener('click', once, true)
    document.removeEventListener('keydown', once, true)
  }
  document.addEventListener('click', once, true)
  document.addEventListener('keydown', once, true)
}

export async function startMerchantWebAlertPlayer() {
  stopMerchantWebAlertPlayer()
  await loadConfig()
  if (!config || !config.web_enabled) return

  if (readUnlockSession()) {
    unlocked = true
  }

  baselineReady = false
  lastWsNewAt = 0
  lastWsCancelAt = 0

  connectMerchantAlertWs(onWsMerchantAlert)

  await runPeriodicCheck()
  timer = setInterval(runPeriodicCheck, checkIntervalMs)
}

export function stopMerchantWebAlertPlayer() {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
  disconnectMerchantAlertWs()
  baselineReady = false
  lastWsNewAt = 0
  lastWsCancelAt = 0
  audioNewEl = null
  audioCancelEl = null
}

export async function reloadMerchantWebAlertConfig() {
  await loadConfig()
  if (config && config.web_enabled) {
    rebuildAudioElements()
    if (!timer) {
      await startMerchantWebAlertPlayer()
    } else {
      clearInterval(timer)
      timer = setInterval(runPeriodicCheck, checkIntervalMs)
    }
  }
}
